/**
 * THE ARTICLE'S WEB ADDRESS IN THE EDITOR (register R-019, wave 25c).
 *
 * D28: "slug editable until first publish, frozen after". `AutosaveRequest.slug` carries it, and the
 * platform RESERVES it in the article's address category when the draft saves - releasing the one
 * the article held before (`src/modules/articles/services/article-slug-reservation.ts`). The editor
 * sends the member ONLY WHEN IT CHANGES - and since design 24, T11 nothing changes it: the address is
 * always made from the title at first publication, and the Search pane only shows it
 * (./SearchPanel.tsx). So `slugChange` answers `undefined` and no save sends one; a draft that
 * reserved an address before keeps it.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * ★★ AND THE ADDRESS CATEGORY, WHICH SENT EVERY NEW ARTICLE'S FIRST SUBMIT BACK (design 14, F8)
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * Found on the cabinet walk of 2026-10-01: a new author filled everything, the checklist showed
 * nothing to fix, and "Submit for review" answered 422 `ADDRESS_CATEGORY_REQUIRED` - the article
 * had ONE category ticked and the "Which one goes in the web address" pick was empty. The submit
 * refuses a missing pick before the structural rules are even asked
 * (`refuseUnlessAddressCategoryChosen`, src/modules/articles/services/article-submit.service.ts),
 * and three things on this screen let the author walk into it. Each is fixed below:
 *   1. NOTHING CHOSE THE ONLY POSSIBLE PICK. With one category ticked there is one answer, so the
 *      editor gives it (`chooseSoleAddressCategory`, applied by ./use-editor-draft.ts to every edit
 *      and to the copy it opens). With two or more it is still the author's, and never a guess.
 *   2. THE CHECKLIST DID NOT ASK. It mirrors D21's eight rules and this is not one of them, so it
 *      gains a row of the editor's own (`addressCategoryRow`), counted like the others.
 *   3. THE REFUSAL WAS SHOWN AS THE SERVER'S `detail` ("... (filed under moon)"). It is said in
 *      plain words now (`addressCategoryRequiredMessage`, ../EditorRoute.tsx).
 */
import { hasErrorCode, isApiProblem } from '../../../features/cabinet/api/problem';
import type { ErrorCode } from '../../../features/cabinet/api/generated';
import { articlesT } from '../articles-i18n';
import type { ArticlesT } from '../articles-i18n';
import type { ChecklistRow, DraftBase, EditorDraft } from './document';

/** The three coded 422s the autosave answers an address with. Typed, so a misspelling fails. */
const SLUG_TAKEN: ErrorCode = 'SLUG_TAKEN';
const SLUG_FROZEN: ErrorCode = 'SLUG_FROZEN';
const ADDRESS_CATEGORY_REQUIRED: ErrorCode = 'ADDRESS_CATEGORY_REQUIRED';

/**
 * ★ THE ADDRESS TO SEND: a string to reserve, `null` to give it up (the platform then makes one from
 * the title at first publication), `undefined` to send none - the platform keeps what it holds.
 */
export function slugChange(draft: EditorDraft, base: DraftBase): string | null | undefined {
  const wanted = draft.slug.trim().length === 0 ? null : draft.slug.trim();
  return wanted === base.savedSlug ? undefined : wanted;
}

/** Did the autosave refuse the address it carried? Each of the three is a code, so it is sure. */
export function isSlugRefusal(error: unknown): boolean {
  return (
    isApiProblem(error) &&
    error.status === 422 &&
    (hasErrorCode(error, SLUG_TAKEN) ||
      hasErrorCode(error, SLUG_FROZEN) ||
      hasErrorCode(error, ADDRESS_CATEGORY_REQUIRED))
  );
}

/** What the pane says under the address after a refused save put it back. */
export function slugRefusalMessage(error: unknown, t: ArticlesT = articlesT()): string {
  if (hasErrorCode(error, SLUG_TAKEN)) {
    return t('editor.address.slugTaken');
  }
  if (hasErrorCode(error, SLUG_FROZEN)) {
    return t('editor.address.slugFrozen');
  }
  return t('editor.address.categoryFirst');
}

/* ---------------------------------------------------------------------------------------------
 * The address category (design 14, F8).
 * ------------------------------------------------------------------------------------------- */

/**
 * ★ ONE CATEGORY TICKED AND NONE CHOSEN: THAT ONE IS CHOSEN. The contract refuses a pick outside
 * `categories`, so with exactly one there is nothing to choose between, and leaving it empty only
 * meant a refused submit. Two or more and none chosen: nothing - which of them becomes half of a
 * permanent address is the author's (decision-log row 161, question 8(a)). A pick already made is
 * never touched. The SAME object comes back when nothing changes, so a caller comparing drafts by
 * identity (the autosave's "did that flight carry the newest draft?") sees no change.
 */
export function chooseSoleAddressCategory(draft: EditorDraft): EditorDraft {
  const [only, ...others] = draft.categories;
  if (draft.addressCategory !== null || only === undefined || others.length > 0) {
    return draft;
  }
  return { ...draft, addressCategory: only };
}

/** Is the pick one the submit will take - made, and among the article's own categories? */
function addressCategoryChosen(draft: EditorDraft): boolean {
  return draft.addressCategory !== null && draft.categories.includes(draft.addressCategory);
}

/**
 * ★ THE CHECKLIST'S ROW FOR IT, the editor's own (`EditorRuleName`, ./document.ts). READY once a
 * pick the submit will take is made, NOT YET otherwise - never "cannot check": unlike a D21 rule,
 * nothing about this one comes from a policy that might not have been read. `labelOf` turns a
 * category code into the catalogue's words, as the Setup pane does.
 */
export function addressCategoryRow(
  draft: EditorDraft,
  labelOf: (code: string) => string,
  t: ArticlesT = articlesT(),
): ChecklistRow {
  const chosen = addressCategoryChosen(draft) ? draft.addressCategory : null;
  return {
    rule: 'address_category',
    label: t('editor.address.rowLabel'),
    state: chosen === null ? 'failing' : 'ok',
    // Not a count: there is no number behind this row, so none is shown.
    actual: null,
    min: null,
    max: null,
    detail:
      chosen === null
        ? t('editor.address.rowMissing')
        : t('editor.address.rowChosen', { category: labelOf(chosen) }),
  };
}

/**
 * ★ WHAT A SUBMIT REFUSED FOR IT SAYS - never the server's `detail`, which is written for a log
 * ("choose which of the categories this article is filed under becomes part of ...") and names
 * category CODES. It points at the control that fixes it, by the words on screen: since design 24
 * (T4/T5) that is "Category", whose one pick is the address - the separate select is gone.
 */
export function addressCategoryRequiredMessage(t: ArticlesT): string {
  return t('editor.address.submitRefused');
}

/** Did a submit or resubmit refuse for the address category? A code, so it is sure. */
export function isAddressCategoryRefusal(error: unknown): boolean {
  return hasErrorCode(error, ADDRESS_CATEGORY_REQUIRED);
}
